// 💬 Assistant Chat - Panel de chat del asistente
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mic, Loader2, Sparkles, Info, Lightbulb, X } from 'lucide-react';
import { VoiceSearch } from '@/components/ui/VoiceSearch';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';

export interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
}

interface AssistantChatProps {
  isOpen: boolean;
  messages: Message[];
  onSendMessage: (message: string) => void;
  onClose?: () => void; // ← Agregado
  isProcessing?: boolean;
  suggestions?: string[];
  currentContext?: string;
}

export function AssistantChat({
  isOpen,
  messages,
  onSendMessage,
  onClose,
  isProcessing = false,
  suggestions = [],
  currentContext = '',
}: AssistantChatProps) {
  const [inputValue, setInputValue] = useState('');
  const [showVoice, setShowVoice] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll al último mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus en input cuando se abre
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSend = () => {
    if (inputValue.trim() && !isProcessing) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleVoiceTranscript = (text: string) => {
    onSendMessage(text);
    setShowVoice(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="fixed bottom-24 right-6 w-[420px] h-[600px] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden z-40"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-500 to-blue-600 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white">Asistente Virtual</h3>
              <p className="text-xs text-white/80">
                {isProcessing ? 'Pensando...' : 'Estoy aquí para ayudarte'}
              </p>
            </div>
            {/* Botón Cerrar */}
            {onClose && (
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                aria-label="Cerrar asistente"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            )}
          </div>

          {/* Contexto Actual */}
          {currentContext && (
            <div className="px-4 py-2 bg-muted border-b border-border flex items-center gap-2 text-xs">
              <Info className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">
                Estás en: <span className="font-medium text-foreground">{currentContext}</span>
              </span>
            </div>
          )}

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900/20 dark:to-blue-900/20 flex items-center justify-center">
                  <Sparkles className="h-8 w-8 text-purple-500" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2">¡Hola! 👋</h4>
                  <p className="text-sm text-muted-foreground max-w-xs">
                    Soy tu asistente virtual. Puedo ayudarte a navegar, buscar información, crear registros y mucho más.
                  </p>
                </div>
              </div>
            ) : (
              messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))
            )}

            {/* Typing indicator */}
            {isProcessing && (
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div className="bg-muted rounded-2xl rounded-tl-none px-4 py-2 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Pensando...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="px-4 py-2 border-t border-border">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="h-3 w-3 text-yellow-500" />
                <span className="text-xs font-medium text-muted-foreground">Sugerencias:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => onSendMessage(suggestion)}
                    className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 border-t border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe tu mensaje..."
                disabled={isProcessing}
                className="flex-1"
              />

              {/* Voice Search Button */}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowVoice(!showVoice)}
                disabled={isProcessing}
                className="relative"
              >
                <Mic className="h-4 w-4" />
              </Button>

              {/* Send Button */}
              <Button
                type="button"
                size="sm"
                onClick={handleSend}
                disabled={!inputValue.trim() || isProcessing}
                className="gap-2"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>

            {/* Voice Search Component */}
            {showVoice && (
              <div className="mt-2">
                <VoiceSearch
                  onTranscript={handleVoiceTranscript}
                  placeholder="Di algo..."
                  language="es-ES"
                />
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Componente de burbuja de mensaje individual
 */
function MessageBubble({ message }: { message: Message }) {
  const isUser = message.type === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn('flex items-start gap-2', isUser && 'flex-row-reverse')}
    >
      {/* Avatar */}
      <div
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-gradient-to-br from-purple-500 to-blue-600'
        )}
      >
        {isUser ? (
          <span className="text-xs font-bold">
            {message.content.charAt(0).toUpperCase()}
          </span>
        ) : (
          <Sparkles className="h-4 w-4 text-white" />
        )}
      </div>

      {/* Message Content */}
      <div
        className={cn(
          'max-w-[75%] rounded-2xl px-4 py-2',
          isUser
            ? 'bg-primary text-primary-foreground rounded-tr-none'
            : 'bg-muted rounded-tl-none'
        )}
      >
        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        <span className="text-[10px] opacity-70 mt-1 block">
          {message.timestamp.toLocaleTimeString('es-PE', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </div>
    </motion.div>
  );
}
